import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import {
  addGeometry,
  deleteGeometry,
  selectGeometry,
  extrudeGeometry,
  flattenGeometry,
  updateDimensions,
  updateCircleRadius,
  updateSplinePoints
} from '@state/slices/geometrySlice';
import type {
  ConstructionGeometry,
  RectangleGeometry,
  CircleGeometry,
  SplineGeometry
} from '@contracts/index';
import {
  MIN_RECTANGLE_WIDTH_M,
  MIN_RECTANGLE_DEPTH_M,
  MIN_CIRCLE_RADIUS_M,
  isValidBuildingMassHeight
} from '@contracts/index';

type GeometryTypeChoice = 'rectangle' | 'circle' | 'spline';

export const GeometryInspector: React.FC = () => {
  const dispatch = useAppDispatch();
  const geometryEntities = useAppSelector((state) => state.geometry.entities);
  const geometryIds = useAppSelector((state) => state.geometry.ids);
  const selectedGeometryId = useAppSelector((state) => state.geometry.selectedId);

  const selectedEntity = selectedGeometryId ? geometryEntities[selectedGeometryId] : null;

  // Local state for numeric editing of the selected entity
  const [widthInput, setWidthInput] = useState<string>('5');
  const [depthInput, setDepthInput] = useState<string>('5');
  const [radiusInput, setRadiusInput] = useState<string>('3');
  const [heightInput, setHeightInput] = useState<string>('5');

  // Sync inputs when selected geometry changes
  useEffect(() => {
    if (!selectedEntity) return;

    if (selectedEntity.type === 'rectangle' || selectedEntity.type === 'building_mass') {
      setWidthInput(selectedEntity.width.toFixed(1));
      setDepthInput(selectedEntity.depth.toFixed(1));
    } else if (selectedEntity.type === 'circle') {
      const r = (selectedEntity as CircleGeometry).radius ?? 3;
      setRadiusInput(r.toFixed(1));
    }

    const currentHeight = selectedEntity.height ?? (selectedEntity.type === 'building_mass' ? selectedEntity.height : 0);
    setHeightInput(currentHeight > 0 ? currentHeight.toFixed(1) : '5');
  }, [selectedGeometryId, selectedEntity]);

  const handleCreateGeometry = (typeToCreate: GeometryTypeChoice = 'rectangle') => {
    const timestamp = Date.now().toString().slice(-6);
    const offset = (geometryIds.length % 5) * 6; // stagger slightly if multiple objects exist

    if (typeToCreate === 'rectangle') {
      const newRect: RectangleGeometry = {
        id: `rect-${timestamp}`,
        type: 'rectangle',
        center: { x: offset, y: 0, z: offset },
        width: 5,
        depth: 5,
        rotationY: 0
      };
      dispatch(addGeometry(newRect));
    } else if (typeToCreate === 'circle') {
      const newCircle: CircleGeometry = {
        id: `circle-${timestamp}`,
        type: 'circle',
        center: { x: offset, y: 0, z: offset },
        radius: 3,
        rotationY: 0,
        width: 6,
        depth: 6
      };
      dispatch(addGeometry(newCircle));
    } else if (typeToCreate === 'spline') {
      const newSpline: SplineGeometry = {
        id: `spline-${timestamp}`,
        type: 'spline',
        center: { x: offset, y: 0, z: offset },
        points: [
          { x: offset - 15, y: 0, z: offset - 10 },
          { x: offset, y: 0, z: offset - 15 },
          { x: offset + 15, y: 0, z: offset - 5 },
          { x: offset + 10, y: 0, z: offset + 15 },
          { x: offset - 10, y: 0, z: offset + 10 }
        ],
        rotationY: 0,
        width: 30,
        depth: 30
      };
      dispatch(addGeometry(newSpline));
    }
  };

  // Dimension Handlers for Rectangle
  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valStr = e.target.value;
    setWidthInput(valStr);
    const val = parseFloat(valStr);
    if (selectedEntity && Number.isFinite(val) && val >= MIN_RECTANGLE_WIDTH_M) {
      dispatch(updateDimensions({ id: selectedEntity.id, width: val }));
    }
  };

  const handleDepthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valStr = e.target.value;
    setDepthInput(valStr);
    const val = parseFloat(valStr);
    if (selectedEntity && Number.isFinite(val) && val >= MIN_RECTANGLE_DEPTH_M) {
      dispatch(updateDimensions({ id: selectedEntity.id, depth: val }));
    }
  };

  // Dimension Handler for Circle
  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valStr = e.target.value;
    setRadiusInput(valStr);
    const val = parseFloat(valStr);
    if (selectedEntity && Number.isFinite(val) && val >= MIN_CIRCLE_RADIUS_M) {
      dispatch(updateCircleRadius({ id: selectedEntity.id, radius: val }));
    }
  };

  // Generic Extrusion Handlers (applies to Rectangle, Circle, and Spline)
  const isSelectedExtruded = selectedEntity
    ? (selectedEntity.height !== undefined && selectedEntity.height > 0) || selectedEntity.type === 'building_mass'
    : false;

  const handleToggleExtrusion = () => {
    if (!selectedEntity) return;

    if (isSelectedExtruded) {
      dispatch(flattenGeometry(selectedEntity.id));
    } else {
      const h = parseFloat(heightInput);
      const targetHeight = isValidBuildingMassHeight(h) ? h : 5.0;
      dispatch(extrudeGeometry({ id: selectedEntity.id, height: targetHeight }));
    }
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valStr = e.target.value;
    setHeightInput(valStr);
    const val = parseFloat(valStr);
    if (selectedEntity && isSelectedExtruded && isValidBuildingMassHeight(val)) {
      dispatch(extrudeGeometry({ id: selectedEntity.id, height: val }));
    }
  };

  const handleDeleteSelected = () => {
    if (!selectedEntity) return;
    dispatch(deleteGeometry(selectedEntity.id));
  };

  // Spline scale adjustment
  const handleScaleSpline = (factor: number) => {
    if (!selectedEntity || selectedEntity.type !== 'spline') return;
    const currentPoints = (selectedEntity as SplineGeometry).points || [];
    const center = selectedEntity.center;
    const updated = currentPoints.map((p) => ({
      x: center.x + (p.x - center.x) * factor,
      y: 0 as const,
      z: center.z + (p.z - center.z) * factor
    }));
    dispatch(updateSplinePoints({ id: selectedEntity.id, points: updated }));
  };

  const getGeometryName = (geom: ConstructionGeometry, idx: number): string => {
    if (geom.type === 'building_mass') return `Rectangle #${idx + 1} (3D Mass)`;
    if (geom.type === 'rectangle') return `Rectangle #${idx + 1}`;
    if (geom.type === 'circle') return `Circle #${idx + 1}`;
    if (geom.type === 'spline') return `Spline #${idx + 1}`;
    return `Object #${idx + 1}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '11.5px' }}>
      {/* 1. Header */}
      <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            color: '#2F78C4',
            letterSpacing: '0.12em',
            marginBottom: '2px'
          }}
        >
          SITE GEOMETRY WORKSPACE
        </div>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
          Site Geometry & Extrusion
        </div>
        <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>
          Create and parameterize 2D footprints and 3D extruded masses.
        </div>
      </div>

      {/* 2. Create Geometry Section */}
      <div
        style={{
          background: 'rgba(248, 250, 252, 0.8)',
          border: '1px solid rgba(47, 120, 196, 0.15)',
          borderRadius: '4px',
          padding: '10px'
        }}
      >
        <div style={{ fontWeight: 600, fontSize: '11px', color: '#1e293b', marginBottom: '8px' }}>
          Create New Geometry
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '8px' }}>
          <button
            type="button"
            id="create-rect-btn"
            onClick={() => handleCreateGeometry('rectangle')}
            style={{
              padding: '6px 4px',
              borderRadius: '4px',
              border: '1px solid #CBD5E1',
              background: '#FFFFFF',
              color: '#0F172A',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px'
            }}
            title="Create 2D construction rectangle"
          >
            <span style={{ fontSize: '14px' }}>⬚</span>
            <span>+ Rectangle</span>
          </button>
          <button
            type="button"
            id="create-circle-btn"
            onClick={() => handleCreateGeometry('circle')}
            style={{
              padding: '6px 4px',
              borderRadius: '4px',
              border: '1px solid #CBD5E1',
              background: '#FFFFFF',
              color: '#0F172A',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px'
            }}
            title="Create 2D circle geometry"
          >
            <span style={{ fontSize: '14px' }}>◯</span>
            <span>+ Circle</span>
          </button>
          <button
            type="button"
            id="create-spline-btn"
            onClick={() => handleCreateGeometry('spline')}
            style={{
              padding: '6px 4px',
              borderRadius: '4px',
              border: '1px solid #CBD5E1',
              background: '#FFFFFF',
              color: '#0F172A',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px'
            }}
            title="Create 2D spline geometry"
          >
            <span style={{ fontSize: '14px' }}>⌇</span>
            <span>+ Spline</span>
          </button>
        </div>
      </div>

      {/* 3. Site Geometry Ownership List (SITE └── Geometry) */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <span style={{ fontWeight: 600, fontSize: '11px', color: '#1e293b' }}>
            Site Geometry Objects ({geometryIds.length})
          </span>
          {selectedGeometryId && (
            <button
              type="button"
              onClick={() => dispatch(selectGeometry(null))}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563EB',
                fontSize: '10px',
                cursor: 'pointer',
                padding: 0
              }}
            >
              Clear Selection
            </button>
          )}
        </div>

        {geometryIds.length === 0 ? (
          <div
            style={{
              padding: '12px',
              textAlign: 'center',
              background: '#F8FAFC',
              borderRadius: '4px',
              color: '#94A3B8',
              fontSize: '11px'
            }}
          >
            No geometry objects on site. Click above to add a Rectangle, Circle, or Spline.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '140px', overflowY: 'auto' }}>
            {geometryIds.map((id, idx) => {
              const geom = geometryEntities[id];
              if (!geom) return null;
              const isSelected = id === selectedGeometryId;
              const isExtruded = (geom.height !== undefined && geom.height > 0) || geom.type === 'building_mass';
              const heightVal = geom.height ?? (geom.type === 'building_mass' ? geom.height : 0);

              return (
                <div
                  key={id}
                  onClick={() => dispatch(selectGeometry(id))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 8px',
                    borderRadius: '4px',
                    border: isSelected ? '1px solid #2563EB' : '1px solid #E2E8F0',
                    background: isSelected ? '#EFF6FF' : '#FFFFFF',
                    cursor: 'pointer',
                    fontSize: '11px'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px' }}>
                      {geom.type === 'circle' ? '◯' : geom.type === 'spline' ? '⌇' : '⬚'}
                    </span>
                    <span style={{ fontWeight: isSelected ? 600 : 500, color: isSelected ? '#1D4ED8' : '#334155' }}>
                      {getGeometryName(geom, idx)}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '9.5px',
                      padding: '1px 5px',
                      borderRadius: '3px',
                      fontWeight: 600,
                      background: isExtruded ? '#ECFDF5' : '#F1F5F9',
                      color: isExtruded ? '#059669' : '#64748B'
                    }}
                  >
                    {isExtruded ? `3D (${heightVal.toFixed(1)}m)` : '2D'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. Selected Geometry Inspector & Generic Extrusion Controls */}
      {selectedEntity ? (
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(47, 120, 196, 0.25)',
            borderRadius: '4px',
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: '12px', color: '#0F172A' }}>
              Properties — {getGeometryName(selectedEntity, geometryIds.indexOf(selectedEntity.id))}
            </span>
            <span
              style={{
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '3px',
                fontWeight: 600,
                background: isSelectedExtruded ? '#ECFDF5' : '#EFF6FF',
                color: isSelectedExtruded ? '#059669' : '#1D4ED8'
              }}
            >
              {isSelectedExtruded ? '3D EXTRUDED' : '2D FOOTPRINT'}
            </span>
          </div>

          {/* Geometry-Specific Dimension Controls */}
          {(selectedEntity.type === 'rectangle' || selectedEntity.type === 'building_mass') && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <label
                  htmlFor="geom-width-input"
                  style={{ display: 'block', fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '2px' }}
                >
                  Width (m) [X]
                </label>
                <input
                  id="geom-width-input"
                  type="number"
                  step="0.5"
                  min={MIN_RECTANGLE_WIDTH_M}
                  value={widthInput}
                  onChange={handleWidthChange}
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #CBD5E1',
                    fontSize: '11px',
                    outline: 'none'
                  }}
                />
                <span style={{ fontSize: '9px', color: '#94A3B8' }}>Min {MIN_RECTANGLE_WIDTH_M}m</span>
              </div>
              <div>
                <label
                  htmlFor="geom-depth-input"
                  style={{ display: 'block', fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '2px' }}
                >
                  Depth (m) [Z]
                </label>
                <input
                  id="geom-depth-input"
                  type="number"
                  step="0.5"
                  min={MIN_RECTANGLE_DEPTH_M}
                  value={depthInput}
                  onChange={handleDepthChange}
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #CBD5E1',
                    fontSize: '11px',
                    outline: 'none'
                  }}
                />
                <span style={{ fontSize: '9px', color: '#94A3B8' }}>Min {MIN_RECTANGLE_DEPTH_M}m</span>
              </div>
            </div>
          )}

          {selectedEntity.type === 'circle' && (
            <div>
              <label
                htmlFor="geom-radius-input"
                style={{ display: 'block', fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '2px' }}
              >
                Radius (m)
              </label>
              <input
                id="geom-radius-input"
                type="number"
                step="0.5"
                min={MIN_CIRCLE_RADIUS_M}
                value={radiusInput}
                onChange={handleRadiusChange}
                style={{
                  width: '100%',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  border: '1px solid #CBD5E1',
                  fontSize: '11px',
                  outline: 'none'
                }}
              />
              <span style={{ fontSize: '9px', color: '#94A3B8' }}>
                Diameter: {(parseFloat(radiusInput || '0') * 2).toFixed(1)}m · Min {MIN_CIRCLE_RADIUS_M}m
              </span>
            </div>
          )}

          {selectedEntity.type === 'spline' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#64748b' }}>
                  Spline Control Points ({(selectedEntity as SplineGeometry).points?.length || 0})
                </span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => handleScaleSpline(0.9)}
                    style={{
                      padding: '1px 5px',
                      fontSize: '10px',
                      borderRadius: '3px',
                      border: '1px solid #CBD5E1',
                      background: '#FFFFFF',
                      cursor: 'pointer'
                    }}
                    title="Scale spline smaller"
                  >
                    -10%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleScaleSpline(1.1)}
                    style={{
                      padding: '1px 5px',
                      fontSize: '10px',
                      borderRadius: '3px',
                      border: '1px solid #CBD5E1',
                      background: '#FFFFFF',
                      cursor: 'pointer'
                    }}
                    title="Scale spline larger"
                  >
                    +10%
                  </button>
                </div>
              </div>
              <div
                style={{
                  background: '#F8FAFC',
                  borderRadius: '3px',
                  padding: '4px 6px',
                  fontSize: '9.5px',
                  color: '#64748B',
                  fontFamily: 'monospace'
                }}
              >
                {((selectedEntity as SplineGeometry).points || []).map((p, i) => (
                  <div key={i}>
                    P{i + 1}: ({p.x.toFixed(1)}m, {p.z.toFixed(1)}m)
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 5. Generic Extrusion Controls (Generic to Rectangle, Circle, Spline) */}
          <div
            style={{
              borderTop: '1px solid #E2E8F0',
              paddingTop: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '10.5px', fontWeight: 600, color: '#1E293B' }}>
                Extrusion (3D Building Height)
              </span>
              <button
                type="button"
                id="toggle-extrusion-btn"
                onClick={handleToggleExtrusion}
                style={{
                  padding: '3px 8px',
                  borderRadius: '3px',
                  border: isSelectedExtruded ? '1px solid #CBD5E1' : '1px solid #059669',
                  background: isSelectedExtruded ? '#F8FAFC' : '#ECFDF5',
                  color: isSelectedExtruded ? '#64748B' : '#047857',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isSelectedExtruded ? 'Flatten to 2D' : '⤉ Extrude to 3D'}
              </button>
            </div>

            {isSelectedExtruded && (
              <div>
                <label
                  htmlFor="geom-height-input"
                  style={{ display: 'block', fontSize: '10px', fontWeight: 600, color: '#64748b', marginBottom: '2px' }}
                >
                  Extrude Height (m) [Y]
                </label>
                <input
                  id="geom-height-input"
                  type="number"
                  step="0.5"
                  min="0.1"
                  value={heightInput}
                  onChange={handleHeightChange}
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #CBD5E1',
                    fontSize: '11px',
                    outline: 'none'
                  }}
                />
                <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                  {[5, 10, 20, 35, 50].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => {
                        setHeightInput(preset.toString());
                        dispatch(extrudeGeometry({ id: selectedEntity.id, height: preset }));
                      }}
                      style={{
                        padding: '1px 5px',
                        fontSize: '9.5px',
                        borderRadius: '3px',
                        border: '1px solid #CBD5E1',
                        background: '#FFFFFF',
                        color: '#475569',
                        cursor: 'pointer'
                      }}
                    >
                      {preset}m
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Delete Action */}
          <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              id="delete-geom-btn"
              onClick={handleDeleteSelected}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 8px',
                borderRadius: '3px',
                border: '1px solid #FCA5A5',
                background: '#FEF2F2',
                color: '#DC2626',
                cursor: 'pointer',
                fontSize: '10.5px',
                fontWeight: 600
              }}
            >
              <span>✕</span>
              <span>Delete Geometry</span>
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            padding: '10px',
            background: 'rgba(248, 250, 252, 0.7)',
            borderRadius: '4px',
            border: '1px dashed #CBD5E1',
            textAlign: 'center',
            fontSize: '10.5px',
            color: '#64748B'
          }}
        >
          Select an existing geometry from the list or viewport to view and edit its parameters.
        </div>
      )}
    </div>
  );
};
